import { ILocation } from 'src/types/graphql'
import { prettifyDirectoryText } from 'src/utils/directoryText'

const SITE = 'https://www.spotme.mx'

/**
 * `LocalBusiness` markup for a published ficha (plan §2.5).
 *
 * Deliberately minimal. It describes the FACILITY — name, where it is, and the
 * page that is about it — and nothing else:
 *   · no `aggregateRating` — we have no reviews of an unclaimed business, and
 *     inventing one would be a fabricated record.
 *   · no `priceRange` — the price on the page is the operator's own, carrying
 *     its own attribution and capture date. Lifting it into structured data
 *     strips that attribution and turns it into a SpotMe price claim, which is
 *     the four-vertex drift the root rules exist to prevent.
 *   · no `telephone` — the contact bundle is gated behind the reveal, and
 *     JSON-LD is served to everyone.
 *
 * Detail pages are `noindex` at launch, so nothing here is indexed yet; the
 * markup is correct for the moment that lifts.
 */
export function buildDirectoryLocalBusinessJsonLd(
  location: Pick<
    ILocation,
    'title' | 'address' | 'coordinates' | 'addressDetails'
  >,
  fichaPath: string
): Record<string, unknown> | null {
  const { title, address, coordinates, addressDetails } = location
  // `name` is required and must be non-empty. Imported rows come from scraped
  // + DENUE data, so a blank title is possible — emit nothing rather than a
  // structurally invalid LocalBusiness record.
  // Same display clean-up as the visible page, so the markup and the H1 agree.
  const name = prettifyDirectoryText(title)
  if (!name) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    url: `${SITE}${fichaPath}`,
    ...(address || addressDetails?.city
      ? {
          address: {
            '@type': 'PostalAddress',
            ...(address
              ? { streetAddress: prettifyDirectoryText(address) }
              : {}),
            ...(addressDetails?.city
              ? { addressLocality: addressDetails.city }
              : {}),
            ...(addressDetails?.state
              ? { addressRegion: addressDetails.state }
              : {}),
            addressCountry: 'MX'
          }
        }
      : {}),
    ...(coordinates
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: coordinates.lat,
            longitude: coordinates.lng
          }
        }
      : {})
  }
}

type Crumb = { name: string; path: string }

/**
 * `BreadcrumbList` for a directory page. The last entry is the CURRENT page —
 * on a ficha that is the facility itself, which the visible crumb deliberately
 * omits (it ends at the city), so this is built from its own list rather than
 * mirroring the UI.
 */
export function buildDirectoryBreadcrumbJsonLd(
  crumbs: Crumb[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: `${SITE}${crumb.path}`
    }))
  }
}
